import { useState } from 'react';

import { ApiError, type PhotoOut } from '@solar/api-client';
import { getLocation, takePhoto } from '@solar/native-bridge';

import { api } from '../api';

interface Props {
  orderId: number;
  slot: number;
  label: string;
  required: boolean;
  existing: PhotoOut | undefined;
  onUploaded: (photo: PhotoOut) => void;
}

export function PhotoSlot({ orderId, slot, label, required, existing, onUploaded }: Props) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<string | null>(null);

  async function capture() {
    setBusy(true);
    setError(null);
    try {
      const photo = await takePhoto();
      // 즉시 미리보기를 보여준다 — 3G 현장에서 업로드가 끝날 때까지 기다리면
      // 기사가 사진이 찍혔는지 알 수 없어 같은 슬롯을 반복 촬영한다.
      setPreview(photo.dataUrl);

      // 위치는 실패해도 업로드를 막지 않는다 (지하 주차장 등에서 자주 실패).
      const coords = photo.lat !== undefined ? null : await getLocation().catch(() => null);

      const saved = await api.uploadPhoto(orderId, slot, photo.dataUrl, {
        takenAt: photo.takenAt,
        lat: photo.lat ?? coords?.lat,
        lng: photo.lng ?? coords?.lng,
      });
      onUploaded(saved);
    } catch (err) {
      setPreview(null);
      setError(err instanceof ApiError ? err.message : '사진을 저장하지 못했습니다.');
    } finally {
      setBusy(false);
    }
  }

  const src = preview ?? existing?.url ?? null;

  return (
    <div className={src ? 'photo filled' : 'photo'}>
      <button type="button" onClick={capture} disabled={busy}>
        {src ? (
          <img src={src} alt={label} loading="lazy" />
        ) : (
          <span className="photo__placeholder">+</span>
        )}
        <span className="photo__label">
          {label}
          {required && !src && <em> *</em>}
        </span>
      </button>
      {busy && <span className="photo__busy">업로드 중…</span>}
      {error && <span className="photo__error">{error}</span>}
    </div>
  );
}
